import React from 'react';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import { Table, TableBody, TableContainer, TableHead, TableRow, TableCell } from '@mui/material';
import styled, { keyframes } from 'styled-components';

const TableTemplate = ({ buttonHaver: ButtonHaver, columns, rows }) => {
    return (
        <StyledTableContainer>
            <Table stickyHeader aria-label="data table">
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <StyledHeaderCell
                                key={column.id}
                                align={column.align || 'left'}
                                style={{ minWidth: column.minWidth }}
                            >
                                {column.label}
                            </StyledHeaderCell>
                        ))}
                        <StyledHeaderCell align="center">Actions</StyledHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <EmptyCell colSpan={columns.length + 1} align="center">
                                No records found
                            </EmptyCell>
                        </TableRow>
                    ) : (
                        rows.map((row, rowIndex) => (
                            <StyledBodyRow key={row.id || rowIndex} hover>
                                {columns.map((column) => {
                                    const value = row[column.id];

                                    if (column.render) {
                                        return (
                                            <StyledBodyCell key={column.id} align={column.align || 'left'}>
                                                {column.render(value, row)}
                                            </StyledBodyCell>
                                        );
                                    }

                                    if (column.id === 'photo') {
                                        return (
                                            <StyledBodyCell key={column.id} align="center">
                                                {value ? (
                                                    <Avatar
                                                        src={`${process.env.REACT_APP_BASE_URL}/${value}`}
                                                        sx={{ width: 40, height: 40, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                                                    />
                                                ) : (
                                                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.3)' }}>
                                                        <PersonIcon sx={{ fontSize: 20 }} />
                                                    </Avatar>
                                                )}
                                            </StyledBodyCell>
                                        );
                                    }
                                    return (
                                        <StyledBodyCell key={column.id} align={column.align || 'left'}>
                                            {column.format && typeof value === 'number'
                                                ? column.format(value)
                                                : value ?? '—'}
                                        </StyledBodyCell>
                                    );
                                })}
                                <StyledBodyCell align="center">
                                    <ButtonHaver row={row} />
                                </StyledBodyCell>
                            </StyledBodyRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </StyledTableContainer>
    );
};

export default TableTemplate;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledTableContainer = styled(TableContainer)`
    border-radius: 16px !important;
    border: 1px solid #edf2f7;
    animation: ${fadeIn} 0.4s ease-out;
    overflow: hidden;
    
    & .MuiTable-root {
        border-collapse: separate;
        border-spacing: 0;
    }
`;

const StyledHeaderCell = styled(TableCell)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    color: rgba(255,255,255,0.85) !important;
    font-weight: 600 !important;
    font-size: 0.8rem !important;
    letter-spacing: 0.06em !important;
    text-transform: uppercase !important;
    padding: 14px 16px !important;
    border-bottom: none !important;
    white-space: nowrap;
`;

const StyledBodyRow = styled(TableRow)`
    transition: background 0.15s ease;
    
    &:nth-child(even) {
        background-color: #f8fafc;
    }
    
    &:hover {
        background-color: #eef2ff !important;
    }
    
    &:last-child td {
        border-bottom: none;
    }
`;

const StyledBodyCell = styled(TableCell)`
    padding: 12px 16px !important;
    font-size: 0.875rem !important;
    color: #374151 !important;
    border-bottom: 1px solid #f0f4f8 !important;
    vertical-align: middle !important;
`;

const EmptyCell = styled(TableCell)`
    padding: 48px 16px !important;
    color: #9ca3af !important;
    font-size: 0.95rem !important;
    font-style: italic;
    border-bottom: none !important;
`;